import { useCallback, useEffect, useRef, useState } from "react";
import { useAmbient } from "../../../../components/Motion/useAmbient";
import frame1 from "./frames/frame-1-928.webp";
import frame2 from "./frames/frame-2-928.webp";
import frame3 from "./frames/frame-3-928.webp";
import frame4 from "./frames/frame-4-928.webp";
import frame5 from "./frames/frame-5-928.webp";
import frame1Small from "./frames/frame-1-406.webp";
import frame2Small from "./frames/frame-2-406.webp";
import frame3Small from "./frames/frame-3-406.webp";
import frame4Small from "./frames/frame-4-406.webp";
import frame5Small from "./frames/frame-5-406.webp";

// The blinking eye is five drawings played in a 14-step sequence. They are
// pre-rendered WebP (scripts/optimize-luca-frames.mjs), not the original SVGs in
// public/Assets/images/luca animation/, and that is the whole performance story
// of this component: each SVG carries 37 filters and 17 blend modes, so showing
// one through an <img> meant the main thread re-rasterised all of it on every
// swap, and preloading all fourteen parsed them in a single ~1s task - a visible
// freeze the moment the eye came near the viewport, measured on desktop.
//
// A bitmap is decoded once, off the main thread (img.decode() below), and every
// later swap only draws pixels that are already decoded. The loop itself stayed
// cheap on purpose: a ~4Hz timer that runs only while the eye is on screen and
// swaps the <img> through a ref, with no re-render.
const LARGE = [frame1, frame2, frame3, frame4, frame5];
const SMALL = [frame1Small, frame2Small, frame3Small, frame4Small, frame5Small];
// Which drawing each step shows. The script checks this against the SVGs.
const SEQ = [0, 1, 2, 3, 4, 4, 3, 4, 3, 4, 3, 2, 1, 0];
const LOOP_MS = 3300; // one full blink cycle (matches the legacy timing)
const FRAME_MS = LOOP_MS / SEQ.length;
// Up to 460px in the desktop column (TechSkills, md and up), 130px on phones.
const SIZES = "(min-width: 900px) 460px, 130px";
const srcSetFor = (i) => `${SMALL[i]} 406w, ${LARGE[i]} 928w`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const LucaEyeAnimation = ({ maxWidth = "400px" }) => {
  const imgRef = useRef(null);
  const frameRef = useRef(0);
  const timerRef = useRef(null);
  const visibleRef = useRef(false);
  // Holding the decoded images keeps them in the memory cache while the eye runs.
  const decodedRef = useRef([]);
  const [loaded, setLoaded] = useState(false);
  const [near, setNear] = useState(false);

  const stop = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const start = useCallback(() => {
    if (timerRef.current || !visibleRef.current || !loaded) return;
    if (prefersReducedMotion()) return; // stays on the resting frame
    timerRef.current = setInterval(() => {
      frameRef.current = (frameRef.current + 1) % SEQ.length;
      const img = imgRef.current;
      if (!img) return;
      const i = SEQ[frameRef.current];
      img.srcset = srcSetFor(i);
      img.src = LARGE[i];
    }, FRAME_MS);
  }, [loaded]);

  // Wide margin: start decoding frames well before the eye scrolls in.
  const nearRef = useAmbient({
    rootMargin: "600px",
    onChange: useCallback((inView) => {
      if (inView) setNear(true);
    }, []),
  });

  // Tight margin: only blink while actually on screen.
  const visibleObserverRef = useAmbient({
    rootMargin: "0px",
    onChange: useCallback(
      (inView) => {
        visibleRef.current = inView;
        if (inView) start();
        else stop();
      },
      [start]
    ),
  });

  const imgCallbackRef = useCallback(
    (node) => {
      imgRef.current = node;
      visibleObserverRef(node);
    },
    [visibleObserverRef]
  );

  useEffect(() => {
    if (!near || loaded) return undefined;
    let cancelled = false;
    Promise.all(
      LARGE.map((src, i) => {
        const img = new Image();
        img.sizes = SIZES;
        img.srcset = srcSetFor(i);
        img.src = src;
        decodedRef.current[i] = img;
        // A frame that fails to decode should not freeze the eye.
        return img.decode().catch(() => {});
      })
    ).then(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [near, loaded]);

  useEffect(() => {
    start();
    return stop;
  }, [start]);

  return (
    <div
      ref={nearRef}
      style={{
        width: "100%",
        maxWidth,
        aspectRatio: "1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        ref={imgCallbackRef}
        src={LARGE[0]}
        srcSet={srcSetFor(0)}
        sizes={SIZES}
        width={928}
        height={1024}
        loading="lazy"
        decoding="async"
        alt="Luca blinking eye animation"
        style={{ width: "100%", height: "auto" }}
      />
    </div>
  );
};

export default LucaEyeAnimation;
